import classnames from 'classnames';
import React, { memo, useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import dayjs from 'dayjs';
import { PCStoreJsBridge } from '../nativeapi';
import { cacheKeys, localKeys } from '../constants/storageKeys';
import styles from './Operation.module.less';
import './animation.css';
import {
  setDownloadVisible,
  setPinPopVisible,
  setPinPopCloseVisible,
  setOperationMenuVisible,
  setNewVersionInfo,
} from '../actions/layout';
import { APP_VERSION, APP_VERSION_D } from '../config';
import { DOWNLOAD_FINISHED, DOWNLOAD_ING } from '../constants/softstatus';

import { logging } from '../actions/log';
import { getFeedbackHot } from '../actions/userFeedback';
import { logNative } from '../utils/utils';
import { SIM_DOWNLOAD_ING } from '../constants/asimstatus';
import User from './User';

const selectDownloadVisible = createSelector(
  state => state.layout,
  layout => layout.downloadVisible,
);

const selectOperationMenuVisible = createSelector(
  state => state.layout,
  layout => layout.OperationMenuVisible,
);

const selectDownloadSofts = createSelector(
  state => state.download,
  download => download.softs,
);

const selectNewVersionInfo = createSelector(
  state => state.layout,
  Layout => Layout.newVersionInfo,
);

const selectSIMStatus = createSelector(
  state => state.asimstatus,
  asimstatus => asimstatus.status,
);

const selectFeedbackHot = createSelector(
  state => state.userFeedback,
  userFeedback => userFeedback.feedbackHot,
);
const selectUserInfo = createSelector(
  state => state.user,
  user => user.userInfo,
);
const selectTopImgVideoVisible = createSelector(
  state => state.layout,
  layout => layout.topImgVideo,
);

export default memo(() => {
  const downVisible = useSelector(selectDownloadVisible);
  const OperationMenuVisible = useSelector(selectOperationMenuVisible);
  const softs = useSelector(selectDownloadSofts); // 用于判断是否展示正在下載的数目
  const newVersionInfo = useSelector(selectNewVersionInfo);
  const SIMStatus = useSelector(selectSIMStatus); // 模拟器当前状态，用于判断是否弹出弹框
  const dispatch = useDispatch();
  const [showZoom, setShowZoom] = useState(false);
  const [isShowHot, setIsShowHot] = useState(false); // 用户判断是否展示小红点
  const feedbackHot = useSelector(selectFeedbackHot);
  const userInfo = useSelector(selectUserInfo);
  const topImgVideo = useSelector(selectTopImgVideoVisible); // 头部图片视频

  /**
   * 初始化、和每次检查更新组件关闭时对比当前是否是最新版本独立包
   * 更新是否需要展示小红点
   */
  useEffect(() => {
    const releaseNotesVersion = localStorage.getItem(cacheKeys.RELEASE_NOTES_VERSION);
    // 获取startInfo
    const startInfoStr = localStorage.getItem(cacheKeys.START_INFO);
    const startInfos = JSON.parse(startInfoStr || '{}');
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const login_status = window.CallNativeMethod && window.CallNativeMethod('get_login_status');

    if (
      (startInfos && startInfos.isDStore && newVersionInfo && newVersionInfo.versionName) ||
      releaseNotesVersion !== (startInfos.isDStore ? APP_VERSION_D : APP_VERSION) ||
      (!(login_status !== '1' || !userInfo.st) && feedbackHot)
    ) {
      setIsShowHot(true);
    } else {
      setIsShowHot(false);
    }
  }, [newVersionInfo, OperationMenuVisible, userInfo, feedbackHot]);

  // 我要反馈屏蔽 需要放开
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const login_status = window.CallNativeMethod && window.CallNativeMethod('get_login_status');
    if (!(login_status !== '1' || !userInfo.st)) {
      dispatch(getFeedbackHot());
    }
  }, [userInfo]);

  useEffect(() => {
    // 获取startInfo
    const startInfoStr = localStorage.getItem(cacheKeys.START_INFO);
    const startInfos = JSON.parse(startInfoStr || '{}');
    if (startInfos.isDStore) {
      PCStoreJsBridge.isNeedUpdatePCStore().then(
        res => {
          if (res && res.versionName) {
            dispatch(setNewVersionInfo(res));
          } else {
            dispatch(setNewVersionInfo(res || {}));
          }
        },
        error => {
          logNative('检测版本更新失败', error);
          dispatch(setNewVersionInfo({ error: true }));
        },
      );
    }
  }, []);
  /**
   * 初始化信息
   */
  useEffect(() => {
    if (softs.length > 0) {
      setShowZoom(true);
    }
  }, [softs.length]);

  const toggleDownload = useCallback(
    ev => {
      // 点击 下载管理 打桩
      dispatch(
        logging({
          category: 'C',
          action: 'D_Manage',
          label: '',
          param2: {
            source: 'HomePage',
          },
        }),
      );
      dispatch(setDownloadVisible(!downVisible));
      dispatch(setOperationMenuVisible(false));
      ev.stopPropagation();
    },
    [dispatch, downVisible],
  );
  const handleMenu = useCallback(
    ev => {
      // 点击 menu 打桩
      dispatch(
        logging({
          category: 'C',
          action: 'OperationMenu',
          label: '',
          param2: {
            source: 'HomePage',
          },
        }),
      );
      dispatch(setOperationMenuVisible(!OperationMenuVisible));
      dispatch(setDownloadVisible(false));
      ev.stopPropagation();
    },
    [dispatch, OperationMenuVisible],
  );

  const handleMini = useCallback(() => {
    // 点击 最小窗口 打桩
    dispatch(
      logging({
        category: 'C',
        action: 'Minimize',
        label: '',
        param2: {
          source: 'HomePage',
        },
      }),
    );
    PCStoreJsBridge.minimizeWindow();
  }, []);

  const handleClose = useCallback(() => {
    // 关闭时统计 正在下载中正在安装中应用个数
    const downloadSofts = [];
    const installSofts = [];
    softs.forEach(soft => {
      if (soft.status === DOWNLOAD_ING) {
        downloadSofts.push(soft.androidType === '1' ? soft.packageName : soft.softID);
      } else if (soft.status === DOWNLOAD_FINISHED) {
        installSofts.push(soft.androidType === '1' ? soft.packageName : soft.softID);
      }
    });
    // 点击 关闭窗口 打桩
    dispatch(
      logging({
        category: 'C',
        action: 'Close',
        label: '',
        param2: {
          source: 'HomePage',
          downloadSofts: downloadSofts.join(','),
          installSofts: installSofts.join(','),
        },
      }),
    );
    if ((downloadSofts && downloadSofts.length > 0) || SIMStatus === SIM_DOWNLOAD_ING) {
      dispatch(setPinPopCloseVisible(true));
      return;
    }
    // 判断桌面图标是否已经存在
    PCStoreJsBridge.checkPINExits()
      .then(res => {
        if (res.PIN) {
          // 是否勾选不再弹出
          const showPinNoMore = localStorage.getItem(localKeys.SHOW_PIN_NO_MORE);
          if (showPinNoMore === 'true') {
            PCStoreJsBridge.closeWindow();
            return;
          }
          dispatch(setPinPopVisible(true));
        } else {
          PCStoreJsBridge.closeWindow();
        }
      })
      .catch(() => {
        PCStoreJsBridge.closeWindow();
      });
  }, [dispatch, dayjs, softs, SIMStatus]);

  const handleAnimationEnd = useCallback(() => {
    setShowZoom(false);
  }, []);

  return (
    <div
      className={
        topImgVideo && topImgVideo.visible && !topImgVideo.isScrolled
          ? styles.operation_white
          : styles.operation
      }
    >
      {window.CallNativeMethod && <User />}
      {softs.length === 0 ? (
        <a className="icon" onClick={toggleDownload}>
          <i className="iconfont icon-download" />
        </a>
      ) : (
        <span
          className={classnames('downanimate', { zoom: showZoom })}
          onAnimationEnd={handleAnimationEnd}
          onClick={toggleDownload}
        >
          <span className="animate-view">
            <span className="animate-wrapper">
              <span className="downnum">{softs.length}</span>
            </span>
          </span>
          <span className={classnames('band')} />
        </span>
      )}

      {/* <a className="icon" onClick={handleAbout}>
        <i className="iconfont icon-menu" />
      </a> */}
      <a className="icon" onClick={handleMenu}>
        {isShowHot && <div className={styles.hot} />}
        <i className="iconfont icon-menu" />
      </a>
      <a className="icon" onClick={handleMini}>
        <i className=" iconfont icon-minimize" />
      </a>
      <a className="icon" onClick={handleClose}>
        <i className="iconfont icon-close" />
      </a>
    </div>
  );
});
